import { useCallback, useEffect, useState } from "react";
import {
  findAllAcoesDisponiveisInscricao,
  findAllModalidadesAcao,
  findAllTemas,
  findAllTipologias,
} from "../../../../API/apiAcoes";
import {
  AcaoDisponivelInscricao,
  Modalidade,
  TemaTurma,
  Tipologia,
} from "../../../../types/acao";

interface FiltrosAcoesDisponiveis {
  searchNome: string;
  searchTipoAcao: number | null;
  searchModalidade: number | null;
  tema: number | null;
  searchPeriodoInicio: string;
  searchPeriodoFim: string;
  ordenacao: string;
}

const filtrosIniciais: FiltrosAcoesDisponiveis = {
  searchNome: "",
  searchTipoAcao: null,
  searchModalidade: null,
  tema: null,
  searchPeriodoInicio: "",
  searchPeriodoFim: "",
  ordenacao: "mais_recentes",
};

export const useAcoesDisponiveis = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingFiltros, setLoadingFiltros] = useState<boolean>(false);
  const [acoesCarregadas, setAcoesCarregadas] = useState(false);
  const [filtrosCarregados, setFiltrosCarregados] = useState(false);
  const [msgError, setMsgError] = useState<string | null>(null);
  const [acoes, setAcoes] = useState<AcaoDisponivelInscricao[]>([]);

  const [tipologias, setTipologias] = useState<Tipologia[]>([]);
  const [modalidades, setModalidades] = useState<Modalidade[]>([]);
  const [temas, setTemas] = useState<TemaTurma[]>([]);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(10);
  const [totalPages, setTotalPages] = useState<number>(1);

  const [filtros, setFiltros] = useState<FiltrosAcoesDisponiveis>(filtrosIniciais);
  const [filtrosAplicados, setFiltrosAplicados] =
    useState<FiltrosAcoesDisponiveis>(filtrosIniciais);

  const carregarAcoes = useCallback(async () => {
    try {
      setLoading(true);
      setMsgError(null);

      const resultado = await findAllAcoesDisponiveisInscricao(
        currentPage,
        perPage,
        filtrosAplicados.searchNome || undefined,
        filtrosAplicados.searchTipoAcao ?? undefined,
        filtrosAplicados.searchModalidade ?? undefined,
        filtrosAplicados.tema ?? undefined,
        filtrosAplicados.searchPeriodoInicio || undefined,
        filtrosAplicados.searchPeriodoFim || undefined,
        filtrosAplicados.ordenacao,
      );

      setAcoes(resultado.acoes || []);
      setTotalPages(resultado.pagination?.last_page || 1);
    } catch (error) {
      setMsgError("Nao foi possivel carregar as acoes disponiveis.");
      setAcoes([]);
    } finally {
      setLoading(false);
      setAcoesCarregadas(true);
    }
  }, [currentPage, perPage, filtrosAplicados]);

  const carregarFiltros = useCallback(async () => {
    try {
      setLoadingFiltros(true);
      const [tipologiasResult, modalidadesResult, temasResult] = await Promise.all([
        findAllTipologias(),
        findAllModalidadesAcao(),
        findAllTemas(),
      ]);

      setTipologias(tipologiasResult);
      setModalidades(modalidadesResult);
      setTemas(temasResult);
    } catch (error) {
      setMsgError("Nao foi possivel carregar os filtros da tela.");
    } finally {
      setLoadingFiltros(false);
      setFiltrosCarregados(true);
    }
  }, []);

  useEffect(() => {
    carregarFiltros();
  }, [carregarFiltros]);

  useEffect(() => {
    carregarAcoes();
  }, [carregarAcoes]);

  const atualizarFiltro = (nome: keyof FiltrosAcoesDisponiveis, valor: string | number | null) => {
    setFiltros((estadoAnterior) => ({
      ...estadoAnterior,
      [nome]: valor,
    }));
  };

  const pesquisar = () => {
    setCurrentPage(1);
    setFiltrosAplicados(filtros);
  };

  const alterarOrdenacao = (ordenacao: string) => {
    const proximosFiltros = { ...filtros, ordenacao };
    setFiltros(proximosFiltros);
    setCurrentPage(1);
    setFiltrosAplicados(proximosFiltros);
  };

  const limparFiltros = () => {
    setFiltros(filtrosIniciais);
    setCurrentPage(1);
    setFiltrosAplicados(filtrosIniciais);
  };

  return {
    loading,
    loadingFiltros,
    loadingInitial: !acoesCarregadas || !filtrosCarregados,
    msgError,
    acoes,
    tipologias,
    modalidades,
    temas,
    currentPage,
    perPage,
    totalPages,
    filtros,
    setCurrentPage,
    setPerPage,
    atualizarFiltro,
    pesquisar,
    alterarOrdenacao,
    limparFiltros,
  };
};
